import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, CheckCircle2, GraduationCap, Quote } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useReveal } from "./Home";
import Seo from "../components/Seo";

const TeamMember = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const ref = useReveal();
  const { t, lp } = useLang();

  const member = t.team.members.find((m) => m.slug === slug);

  if (!member) {
    return (
      <div className="mx-auto max-w-[1200px] px-6 py-40 text-center">
        <h1 className="text-2xl font-bold text-neutral-900">404</h1>
        <button onClick={() => navigate(lp("/team"))} className="mt-4 text-sm font-semibold text-[#F26522]">
          {t.team.backToTeam}
        </button>
      </div>
    );
  }

  const others = t.team.members.filter((m) => m.slug !== slug);

  return (
    <div ref={ref}>
      <Seo title={`${member.name} \u2013 ${t.team.role} | Pure Investments`} description={member.focus} image={member.image} />
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0f1a10] pb-16 pt-36">
        <div className="absolute inset-0 opacity-15">
          <img src={t.hero.slides[2]} alt="" className="h-full w-full object-cover grayscale" />
        </div>
        <div className="relative mx-auto grid max-w-[1200px] items-center gap-10 px-6 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <button onClick={() => navigate(lp("/team"))} className="inline-flex items-center gap-2 text-sm font-medium text-neutral-300 transition-colors hover:text-[#F26522]">
              <ArrowLeft className="h-4 w-4" /> {t.team.backToTeam}
            </button>
            <h1 className="mt-6 text-4xl font-extrabold leading-tight text-white lg:text-5xl">{member.name}</h1>
            <p className="mt-2 text-sm font-semibold uppercase tracking-widest text-[#F26522]">{t.team.role}</p>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-neutral-300">{member.focus}</p>
          </div>
          <div className="lg:col-span-2">
            <div className="mx-auto max-w-sm overflow-hidden rounded-2xl shadow-2xl">
              <img src={member.image} alt={member.name} className="aspect-[4/5] w-full object-cover grayscale" />
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="mx-auto max-w-[1000px] px-6 py-16 lg:py-20">
        {/* Quote */}
        <div className="reveal relative rounded-2xl bg-neutral-50 p-8 lg:p-10">
          <Quote className="absolute right-8 top-8 h-10 w-10 text-[#F26522]/20" />
          <blockquote className="max-w-3xl text-xl italic leading-relaxed text-neutral-800">“{member.quote}”</blockquote>
        </div>

        <div className="mt-14 grid gap-14 lg:grid-cols-2">
          {/* Expertise */}
          <div className="reveal">
            <h2 className="text-2xl font-bold text-neutral-900">{t.team.expertiseTitle}</h2>
            <div className="mt-5 flex flex-wrap gap-2.5">
              {member.expertise.map((e) => (
                <span key={e} className="rounded-full border border-[#F26522]/30 bg-[#F26522]/5 px-4 py-2 text-sm font-medium text-[#c9531a]">
                  {e}
                </span>
              ))}
            </div>

            {member.education && (
              <div className="mt-10">
                <h2 className="flex items-center gap-2 text-2xl font-bold text-neutral-900">
                  <GraduationCap className="h-6 w-6 text-[#F26522]" /> {t.team.educationTitle}
                </h2>
                <ul className="mt-5 space-y-3">
                  {member.education.map((ed) => (
                    <li key={ed} className="text-[15px] leading-relaxed text-neutral-700">{ed}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Highlights */}
          <div className="reveal">
            <h2 className="text-2xl font-bold text-neutral-900">{t.team.highlightsTitle}</h2>
            <ul className="mt-5 space-y-4">
              {member.highlights.map((h) => (
                <li key={h} className="flex gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#F26522]" />
                  <span className="text-[15px] leading-relaxed text-neutral-700">{h}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Other partners */}
      <section className="bg-neutral-50 py-16">
        <div className="mx-auto max-w-[1200px] px-6">
          <h2 className="reveal text-2xl font-bold text-neutral-900">{t.team.title}</h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-3">
            {others.map((m, i) => (
              <button
                key={m.slug}
                onClick={() => navigate(lp(`/team/${m.slug}`))}
                style={{ animationDelay: `${i * 80}ms` }}
                className="reveal group flex items-center gap-4 rounded-xl border border-neutral-100 bg-white p-4 text-left shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <img src={m.image} alt={m.name} className="h-16 w-16 flex-shrink-0 rounded-lg object-cover grayscale transition-all duration-300 group-hover:grayscale-0" />
                <div>
                  <div className="font-bold text-neutral-900 transition-colors group-hover:text-[#F26522]">{m.name}</div>
                  <div className="mt-0.5 text-xs text-neutral-500">{t.team.role}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0f1a10] py-20">
        <div className="reveal mx-auto max-w-2xl px-6 text-center">
          <h2 className="text-3xl font-bold text-white">{t.team.joinTitle}</h2>
          <p className="mt-4 text-[15px] leading-relaxed text-neutral-300">{t.team.joinText}</p>
          <button onClick={() => navigate(lp("/contact"))}
            className="group mt-8 inline-flex items-center gap-2 rounded-md bg-[#F26522] px-8 py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-[#d9561a] hover:shadow-xl">
            {t.team.joinCta}
            <ChevronRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default TeamMember;
